'use client';

import React from 'react';
import { motion } from 'framer-motion';

import { Box, BoxProps } from '../box';
import { Flex } from '../flex';
import { Text } from '../text';
import { Icon } from '../icon';
import { Badge } from '../badge';
import { Skeleton } from '../skeleton';
import { Tooltip } from '../ui/tooltip';

// Motion div for animations (using native div to avoid Chakra prop conflicts)
const MotionDiv = motion.div;

export type InfoFieldSize = 'sm' | 'md' | 'lg';
export type InfoFieldLayout = 'vertical' | 'horizontal' | 'inline';

export interface DetailInfoFieldProps extends Omit<BoxProps, 'children'> {
  /** Field label */
  label: string;
  /** Optional element rendered after the label (e.g. help icon) */
  labelSuffix?: React.ReactNode;
  /** Field value */
  value?: React.ReactNode;
  /** Icon to display */
  icon?: React.ReactNode;
  /** Icon color */
  iconColor?: string;
  /** Size variant */
  size?: InfoFieldSize;
  /** Layout variant */
  layout?: InfoFieldLayout;
  /** Value as badge */
  asBadge?: boolean;
  /** Badge color palette */
  badgeColorPalette?: 'green' | 'red' | 'orange' | 'yellow' | 'blue' | 'purple' | 'gray';
  /** Format value as currency */
  formatAsCurrency?: boolean;
  /** Currency code for formatting */
  currencyCode?: string;
  /** Format value as date */
  formatAsDate?: boolean;
  /** Date format locale */
  dateLocale?: string;
  /** Date format options */
  dateOptions?: Intl.DateTimeFormatOptions;
  /** Format value as number */
  formatAsNumber?: boolean;
  /** Placeholder when value is empty */
  placeholder?: string;
  /** Show copy button */
  copyable?: boolean;
  /** Tooltip content */
  tooltip?: string;
  /** Highlight value */
  highlight?: boolean;
  /** Loading state */
  isLoading?: boolean;
  /** Hide when value is empty */
  hideIfEmpty?: boolean;
  /** Custom render function for value */
  renderValue?: (value: React.ReactNode) => React.ReactNode;
  /** Animation delay in seconds */
  staggerDelay?: number;
}

/**
 * DetailInfoField - Styled info field for detail pages
 * Features multiple layouts, formatting options, and animations
 */
export const DetailInfoField: React.FC<DetailInfoFieldProps> = ({
  label,
  labelSuffix,
  value,
  icon,
  iconColor = '#6B7280',
  size = 'md',
  layout = 'vertical',
  asBadge = false,
  badgeColorPalette = 'gray',
  formatAsCurrency = false,
  currencyCode = 'TRY',
  formatAsDate = false,
  dateLocale = 'tr-TR',
  dateOptions = { year: 'numeric', month: 'long', day: 'numeric' },
  formatAsNumber = false,
  placeholder = '-',
  tooltip,
  highlight = false,
  isLoading = false,
  hideIfEmpty = false,
  renderValue,
  staggerDelay = 0,
  ...boxProps
}) => {
  // Check if value is empty
  const isEmpty =
    value === undefined ||
    value === null ||
    value === '' ||
    (typeof value === 'string' && value.trim() === '');

  // Hide if empty and hideIfEmpty is true
  if (hideIfEmpty && isEmpty) {
    return null;
  }

  // Format value based on options
  const formatValue = (): React.ReactNode => {
    if (isEmpty) return placeholder;

    if (renderValue) {
      return renderValue(value);
    }

    if (formatAsCurrency && (typeof value === 'number' || !isNaN(Number(value)))) {
      return new Intl.NumberFormat('tr-TR', {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(Number(value));
    }

    if (formatAsDate && value) {
      const date = value instanceof Date ? value : new Date(String(value));
      if (!isNaN(date.getTime())) {
        return new Intl.DateTimeFormat(dateLocale, dateOptions).format(date);
      }
    }

    if (formatAsNumber && (typeof value === 'number' || !isNaN(Number(value)))) {
      return new Intl.NumberFormat('tr-TR').format(Number(value));
    }

    return value;
  };

  // Get size styles
  const getSizeStyles = () => {
    switch (size) {
      case 'sm':
        return {
          labelSize: 'xs' as const,
          valueSize: 'sm' as const,
          iconSize: 'sm' as const,
          iconBoxSize: '24px',
          gap: 1,
        };
      case 'lg':
        return {
          labelSize: 'sm' as const,
          valueSize: 'xl' as const,
          iconSize: 'lg' as const,
          iconBoxSize: '40px',
          gap: 3,
        };
      default:
        return {
          labelSize: 'sm' as const,
          valueSize: 'md' as const,
          iconSize: 'md' as const,
          iconBoxSize: '32px',
          gap: 2,
        };
    }
  };

  const sizeStyles = getSizeStyles();
  const formattedValue = formatValue();

  // Animation variants
  const fieldVariants = {
    hidden: { opacity: 0, x: -10 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.3,
        delay: staggerDelay,
        ease: [0.4, 0, 0.2, 1] as [number, number, number, number],
      },
    },
  };

  // Loading skeleton
  if (isLoading) {
    return (
      <Box {...boxProps}>
        <Flex
          direction={layout === 'vertical' ? 'column' : 'row'}
          alignItems={layout === 'vertical' ? 'flex-start' : 'center'}
          gap={sizeStyles.gap}
        >
          {icon && (
            <Skeleton
              width={sizeStyles.iconBoxSize}
              height={sizeStyles.iconBoxSize}
              borderRadius="md"
            />
          )}
          <Box flex={1}>
            <Skeleton width="60px" height="14px" marginBottom={1} />
            <Skeleton width="120px" height="18px" />
          </Box>
        </Flex>
      </Box>
    );
  }

  // Render value content
  const renderValueContent = () => {
    if (asBadge) {
      return (
        <Badge colorPalette={badgeColorPalette} size={size === 'lg' ? 'lg' : 'md'}>
          {formattedValue}
        </Badge>
      );
    }

    return (
      <Text
        fontSize={sizeStyles.valueSize}
        fontWeight={highlight ? 'bold' : 'medium'}
        color={highlight ? 'brand.500' : 'fg.default'}
        lineHeight="tight"
      >
        {formattedValue}
      </Text>
    );
  };

  // Main content
  const content = (
    <MotionDiv variants={fieldVariants} initial="hidden" animate="visible">
      <Box {...boxProps}>
        {layout === 'horizontal' ? (
          // Horizontal layout
          <Flex alignItems="center" gap={3}>
            {icon && (
              <Flex
                alignItems="center"
                justifyContent="center"
                width={sizeStyles.iconBoxSize}
                height={sizeStyles.iconBoxSize}
                borderRadius="md"
                backgroundColor={`${iconColor}15`}
                flexShrink={0}
              >
                <Icon size={sizeStyles.iconSize} style={{ color: iconColor }}>
                  {icon}
                </Icon>
              </Flex>
            )}
            <Flex alignItems="center" justifyContent="space-between" flex={1} gap={2}>
              <Flex alignItems="center" gap={1}>
                <Text fontSize={sizeStyles.labelSize} color="fg.muted">
                  {label}
                </Text>
                {labelSuffix}
              </Flex>
              {renderValueContent()}
            </Flex>
          </Flex>
        ) : layout === 'inline' ? (
          // Inline layout
          <Flex alignItems="center" gap={2} flexWrap="wrap">
            {icon && (
              <Icon size={sizeStyles.iconSize} style={{ color: iconColor }}>
                {icon}
              </Icon>
            )}
            <Flex alignItems="center" gap={1}>
              <Text fontSize={sizeStyles.labelSize} color="fg.muted">
                {label}:
              </Text>
              {labelSuffix}
            </Flex>
            {renderValueContent()}
          </Flex>
        ) : (
          // Vertical layout (default)
          <Flex alignItems="flex-start" gap={3}>
            {icon && (
              <Flex
                alignItems="center"
                justifyContent="center"
                width={sizeStyles.iconBoxSize}
                height={sizeStyles.iconBoxSize}
                borderRadius="md"
                backgroundColor={`${iconColor}15`}
                flexShrink={0}
              >
                <Icon size={sizeStyles.iconSize} style={{ color: iconColor }}>
                  {icon}
                </Icon>
              </Flex>
            )}
            <Box flex={1} minWidth={0}>
              <Flex alignItems="center" gap={1} marginBottom={sizeStyles.gap}>
                <Text fontSize={sizeStyles.labelSize} color="fg.muted">
                  {label}
                </Text>
                {labelSuffix}
              </Flex>
              {renderValueContent()}
            </Box>
          </Flex>
        )}
      </Box>
    </MotionDiv>
  );

  // Wrap with tooltip if provided
  if (tooltip) {
    return <Tooltip content={tooltip}>{content}</Tooltip>;
  }

  return content;
};

export default DetailInfoField;
